import type { Breadcrumb, Event } from '@sentry/react'
import axios from 'axios'

/**
 * PHI safeguards for everything the app sends to Sentry (US 16919).
 *
 * The approach is allow-list, not deny-list: a payload is reduced to fields that are known
 * to be safe (identifiers, status codes, flags) rather than stripped of fields known to be
 * sensitive. Referral payloads grow new patient fields regularly, and a deny-list would
 * silently start leaking the first one nobody added to it.
 *
 * Record identifiers are kept on purpose — they are how an event is traced back to a
 * referral, and on their own they do not identify a patient.
 */

type SafeValue = string | number | boolean | null

export type SafeDetails = Record<string, SafeValue>

/** Keys whose values are safe to send regardless of their name not ending in "id". */
const SAFE_KEYS = new Set(['eventName', 'status', 'statusText', 'type', 'size', 'isSuccess'])

/** `patientId`, `patientFacilityId`, `id`, `referralIds`... but not `idealWeight`. */
const ID_KEY_PATTERN = /(^id$|Id$|Ids$|ID$)/

/**
 * Identifiers that are themselves HIPAA identifiers (member, MRN, SSN, licence numbers)
 * rather than database keys, so the id rule must not let them through.
 */
const PHI_ID_KEY_PATTERN =
  /medicare|medicaid|ssn|mrn|member|policy|subscriber|license|licence|insurance|payer|email|phone/i

/** Backend message codes are short snake/camel words; anything else may be free text. */
const MESSAGE_CODE_PATTERN = /^[A-Za-z_]{1,50}$/

/** Longest string an identifier can plausibly be (a GUID is 36). */
const MAX_ID_LENGTH = 64

const MAX_STACK_LINES = 10

/** Longest error text kept; anything beyond this is a payload dump, not a message. */
const MAX_ERROR_TEXT_LENGTH = 300

/**
 * Error text that carries a serialised value rather than a coded message — a rejected
 * promise holding a response body, for instance — is replaced wholesale.
 */
const SERIALISED_VALUE_PATTERN = /[{}[\]]|"\w+"\s*:/

/**
 * Keeps an error message only when it reads as code-authored text.
 *
 * JS runtime messages ("Cannot read properties of undefined") are safe and worth having;
 * anything holding a serialised object may carry patient data and is dropped whole.
 * @param text the exception value or event message.
 */
export const toSafeErrorText = (text?: string | null): string | undefined => {
  if (!text) return undefined
  if (SERIALISED_VALUE_PATTERN.test(text)) return '[value removed]'
  return text.length > MAX_ERROR_TEXT_LENGTH
    ? `${text.slice(0, MAX_ERROR_TEXT_LENGTH)}…[truncated]`
    : text
}

/**
 * Keys the summarisers below emit whose strings can exceed `MAX_ID_LENGTH` but are already
 * safe (a stack trace, an endpoint path), so the event-level pass must not collapse them.
 */
const SUMMARY_STRING_KEYS = new Set(['stack', 'endpoint', 'message'])

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isSafeScalar = (value: unknown): value is SafeValue =>
  value === null ||
  typeof value === 'number' ||
  typeof value === 'boolean' ||
  (typeof value === 'string' && value.length <= MAX_ID_LENGTH)

/**
 * Drops the query string, which is where search text and filters travel. The path is kept
 * because it carries the endpoint and any record id.
 * @param url absolute or relative URL.
 */
export const stripQueryString = (url?: string): string | undefined => url?.split(/[?#]/)[0]

/**
 * Only a backend message code survives; free-text messages are replaced with a marker.
 * @param message `message` from the API envelope.
 */
export const toSafeMessageCode = (message: unknown): string | undefined => {
  if (typeof message !== 'string' || !message) return undefined
  return MESSAGE_CODE_PATTERN.test(message) ? message : '[free text removed]'
}

const pickSafeFields = (source: Record<string, unknown>): SafeDetails =>
  Object.entries(source).reduce<SafeDetails>((safe, [key, value]) => {
    const isAllowedKey =
      SAFE_KEYS.has(key) || (ID_KEY_PATTERN.test(key) && !PHI_ID_KEY_PATTERN.test(key))
    if (isAllowedKey && isSafeScalar(value)) return { ...safe, [key]: value }
    if (isAllowedKey && Array.isArray(value) && value.every(isSafeScalar)) {
      return { ...safe, [key]: value.join(',') }
    }
    return safe
  }, {})

/**
 * Reduces an arbitrary request payload to its shape and top-level identifiers.
 *
 * Nested objects are not walked — nested referral payloads hold the patient demographics,
 * and the identifiers that matter for tracing sit at the top level.
 * @param payload request body, FormData, File or array passed to a log helper.
 */
export const summarisePayload = (payload: unknown): SafeDetails => {
  if (payload === undefined || payload === null) return { payloadType: 'none' }
  if (typeof FormData !== 'undefined' && payload instanceof FormData) {
    return { payloadType: 'formData' }
  }
  // File names routinely contain the patient's name, so only type and size are kept.
  if (typeof File !== 'undefined' && payload instanceof File) {
    return { payloadType: 'file', fileType: payload.type, size: payload.size }
  }
  if (Array.isArray(payload)) {
    const first = payload.find(isRecord)
    return {
      payloadType: 'array',
      itemCount: payload.length,
      ...(first ? pickSafeFields(first) : {}),
    }
  }
  if (isRecord(payload)) return { payloadType: 'object', ...pickSafeFields(payload) }
  return { payloadType: typeof payload }
}

/**
 * Reduces an axios response to request metadata and the envelope's outcome.
 *
 * The raw response must never be sent: its `config` holds the bearer token and a copy of
 * the request body, and its `data` holds whatever patient record was returned.
 * @param response the value an `HttpClient` call resolved with.
 */
export const summariseResponse = (response: unknown): SafeDetails => {
  if (!isRecord(response)) return summarisePayload(response)

  const config = isRecord(response.config) ? response.config : {}
  const envelope = isRecord(response.data) ? response.data : {}
  const messageCode = toSafeMessageCode(envelope.message)

  return {
    ...(typeof config.method === 'string' ? { method: config.method.toUpperCase() } : {}),
    ...(typeof config.url === 'string' ? { endpoint: stripQueryString(config.url) ?? null } : {}),
    ...(typeof response.status === 'number' ? { status: response.status } : {}),
    ...(typeof envelope.isSuccess === 'boolean' ? { isSuccess: envelope.isSuccess } : {}),
    ...(messageCode ? { messageCode } : {}),
  }
}

const trimStack = (stack?: string): string | undefined =>
  stack?.split('\n').slice(0, MAX_STACK_LINES).join('\n')

/**
 * Reduces a caught error to what is needed to diagnose it.
 *
 * Axios errors carry the request config (token and body) and the response body, so only
 * transport facts are taken from them. For other errors the name and stack are kept; the
 * message is kept only for plain JS errors, whose messages come from code, not data.
 * @param error value from a `catch`.
 */
export const summariseError = (error: unknown): SafeDetails => {
  if (axios.isAxiosError(error)) {
    return {
      errorType: 'axios',
      method: error.config?.method?.toUpperCase() ?? null,
      endpoint: stripQueryString(error.config?.url) ?? null,
      status: error.response?.status ?? null,
      code: error.code ?? null,
      messageCode:
        toSafeMessageCode(
          isRecord(error.response?.data) ? error.response?.data.message : undefined,
        ) ?? null,
    }
  }
  if (error instanceof Error) {
    return {
      errorType: error.name,
      message: error instanceof TypeError || error instanceof RangeError ? error.message : null,
      stack: trimStack(error.stack) ?? null,
    }
  }
  if (isRecord(error)) return { errorType: 'object', ...pickSafeFields(error) }
  return { errorType: typeof error }
}

/**
 * Filters the automatic breadcrumb trail.
 *
 * Console output is dropped outright — the codebase logs response data in places. Click
 * selectors lose their attribute values, which can hold a `title` or `aria-label` with a
 * patient's name. URLs keep their path and lose their query string.
 * @param breadcrumb breadcrumb Sentry recorded automatically.
 */
export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (breadcrumb.category === 'console') return null

  if (breadcrumb.category?.startsWith('ui.')) {
    return { ...breadcrumb, message: breadcrumb.message?.replace(/\[[^\]]*\]/g, '') }
  }

  if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
    return {
      ...breadcrumb,
      data: {
        method: breadcrumb.data?.method,
        url: stripQueryString(breadcrumb.data?.url),
        status_code: breadcrumb.data?.status_code,
      },
    }
  }

  if (breadcrumb.category === 'navigation') {
    return {
      ...breadcrumb,
      data: {
        from: stripQueryString(breadcrumb.data?.from),
        to: stripQueryString(breadcrumb.data?.to),
      },
    }
  }

  return { ...breadcrumb, data: undefined }
}

/**
 * Last line of defence, run on every event before it leaves the browser — including
 * unhandled errors that never pass through the log helpers.
 * @param event error or transaction event about to be sent.
 */
export const scrubEvent = <T extends Event>(event: T): T => {
  const scrubbed: T = { ...event }

  // Exception text is the one path that never passed through the log helpers: an error
  // thrown with a payload in its message would otherwise ship verbatim.
  if (scrubbed.message) {
    scrubbed.message = toSafeErrorText(
      typeof scrubbed.message === 'string' ? scrubbed.message : undefined,
    )
  }

  if (scrubbed.exception?.values) {
    scrubbed.exception = {
      ...scrubbed.exception,
      values: scrubbed.exception.values.map(value => ({
        ...value,
        value: toSafeErrorText(value.value),
      })),
    }
  }

  // Tags and contexts are set by integrations rather than by us; neither is needed to
  // diagnose an error, and both can carry whatever was in scope.
  scrubbed.contexts = undefined

  if (scrubbed.request) {
    scrubbed.request = {
      url: stripQueryString(scrubbed.request.url),
      method: scrubbed.request.method,
    }
  }

  // Our helpers only ever put pre-summarised details in `extra`; anything else that
  // lands there (a third-party integration, a stray setExtra) is reduced the same way.
  if (scrubbed.extra) {
    scrubbed.extra = Object.entries(scrubbed.extra).reduce<Record<string, unknown>>(
      (safe, [key, value]) => ({
        ...safe,
        [key]:
          isSafeScalar(value) || (SUMMARY_STRING_KEYS.has(key) && typeof value === 'string')
            ? value
            : summarisePayload(value),
      }),
      {},
    )
  }

  if (scrubbed.user) {
    scrubbed.user = scrubbed.user.id ? { id: scrubbed.user.id } : undefined
  }

  // Request spans are named "GET <full url>", query string included.
  scrubbed.spans?.forEach(span => {
    const { description } = span
    if (description && /[?#]/.test(description)) span.updateName(description.split(/[?#]/)[0])
    const url: unknown = span.data?.url
    if (typeof url === 'string') span.setData('url', stripQueryString(url))
  })

  if (scrubbed.breadcrumbs) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs
      .map(scrubBreadcrumb)
      .filter((crumb): crumb is Breadcrumb => crumb !== null)
  }

  return scrubbed
}
